import type { QueryClient, QueryKey } from "@tanstack/react-query";

/**
 * Stale-while-revalidate, web side (server: utils/staleHints.ts, 2026-10-31).
 *
 * Dashboard widget GETs send `X-Xale-Allow-Stale: 1`. When the server's fresh
 * entry has expired it answers at once from the widget's last build, says so
 * with `X-Xale-Stale: 1`, and rebuilds in the background. The widget shows
 * that answer immediately and this module asks again a few seconds later, so
 * the screen settles on the fresh numbers without anyone waiting on a cold
 * rebuild. The viewer's own writes are never answered stale (the server
 * checks their write stamp), and no other request opts in.
 */
export const ALLOW_STALE_HEADER = "X-Xale-Allow-Stale";

/** Only dashboard widget reads opt in. */
export function wantsStaleAllowed(method: string | undefined, url: string | undefined): boolean {
  return (method ?? "get").toLowerCase() === "get" && !!url && url.startsWith("/core/dashboard/");
}

/**
 * Whether the response being handled right now is a previous build. EVERY
 * response sets it (axios interceptor), and a query's success callback runs
 * in the same microtask flush as the response that finished it (await →
 * return → React Query's onSuccess) while each XHR completes in a task of
 * its own — so at onSuccess time this names exactly the response that fed the
 * query, whatever its hook does with the payload. (A marker on the payload
 * itself does not work: structural sharing hands the next, fresh answer the
 * same object back. Nor does clearing on a timer: the next response's task
 * can run before the timer's.)
 */
let currentResponseIsStale = false;

/** Axios interceptor, for every response: is this one a previous build? */
export function noteResponse(stale: boolean): void {
  currentResponseIsStale = stale;
}

/** Re-ask after these delays while the server is still rebuilding, then stop. */
const REASK_DELAYS_MS = [3_000, 6_000, 12_000];
const reasks = new Map<string, number>();

/** QueryCache `onSuccess`: a stale answer schedules the next re-ask of that query. */
export function onStaleAwareSuccess(
  queryClient: QueryClient,
  query: { queryHash: string; queryKey: QueryKey },
): void {
  const hash = query.queryHash;
  if (!currentResponseIsStale) {
    reasks.delete(hash);
    return;
  }
  const n = reasks.get(hash) ?? 0;
  if (n >= REASK_DELAYS_MS.length) {
    reasks.delete(hash);
    return;
  }
  reasks.set(hash, n + 1);
  setTimeout(() => {
    void queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
  }, REASK_DELAYS_MS[n]);
}
